import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  jsonLd?: object[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  ogImage,
  twitterCard = 'summary_large_image',
  jsonLd,
}: SEOHeadProps) {
  const scriptRefs = useRef<HTMLScriptElement[]>([]);

  useEffect(() => {
    const existing = document.querySelectorAll('script[data-seo-jsonld]');
    existing.forEach((el) => el.remove());

    if (jsonLd && jsonLd.length > 0) {
      const scripts: HTMLScriptElement[] = [];
      jsonLd.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
        scripts.push(script);
      });
      scriptRefs.current = scripts;
    }

    return () => {
      scriptRefs.current.forEach((s) => s.remove());
      scriptRefs.current = [];
    };
  }, [jsonLd]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={ogUrl || canonical} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}